import { HttpErrorResponse, HttpHeaders, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { WorkflowNoticeService, WorkflowValidationNotice } from './workflow-notice.service';

/** Must match `WorkflowValidationFeedbackFilter.HeaderName` on the API (and its CORS `WithExposedHeaders`). */
export const WORKFLOW_VALIDATION_HEADER = 'X-Workflow-Validation-Failures';

/**
 * docs/design/16_workflow_engine.md §14.3 (Phase 3b) — the admin-authored failure message of a Validation step
 * is shown to the user whose action triggered it, on the very response of that action. The API sends it as one
 * percent-encoded JSON header on ANY workflow-gated response (200, 202, 403, 409, ...), so this reads both the
 * success and the error path, and never changes what the calling screen receives.
 */
export const workflowValidationInterceptor: HttpInterceptorFn = (req, next) => {
  const notices = inject(WorkflowNoticeService);

  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) report(event.headers, notices);
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          report(error.headers, notices);
          // A write step of the workflow failed (422 with a WRITE_* / MAPPED_* code): tell the user which, in their language.
          const code = error.status === 422 ? (error.error as { code?: unknown } | null)?.code : null;
          if (typeof code === 'string' && /^(WRITE_|MAPPED_)/.test(code)) notices.showError(code);
        }
      },
    })
  );
};

function report(headers: HttpHeaders, notices: WorkflowNoticeService): void {
  const raw = headers.get(WORKFLOW_VALIDATION_HEADER);
  if (!raw) return;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as WorkflowValidationNotice[];
    if (Array.isArray(parsed) && parsed.length > 0) notices.show(parsed);
  } catch {
    // A malformed header must never break the user's own action - the permanent copy is the timeline event.
  }
}
